/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { get } from 'utils/lodash'

// View-level (not card-level) board-v2 state that persists across sessions.
// Kept separate from persistentBoardV2CardSlice so column ordering and card
// visibility can evolve independently. This is the first piece of the slice
// 3.2 "state preservation" surface; more view state (scroll, collapsed
// columns) can land here later.

// Column sort modes surfaced by the toolbar Sort dropdown.
//   moreStops — most stops first (the original board default)
//   fewerStops — fewest stops first (the reverse of moreStops)
//   alphabetical — by route name, A–Z
//   alphabeticalDesc — by route name, Z–A
//   custom — preserve the selected-routes order as-is (no reordering)
export const BOARD_V2_SORT_MODES = ['moreStops', 'fewerStops', 'alphabetical', 'alphabeticalDesc', 'custom'] as const

export type BoardV2SortMode = typeof BOARD_V2_SORT_MODES[number]

export type MoveDirection = 'left' | 'right'

export type PersistentBoardV2ViewState = {
  sortMode: BoardV2SortMode
  // User-arranged column order for the `custom` sort mode (route ids). Empty
  // until the user first arranges columns; the container reconciles it against
  // the live selected routes (drops removed ids, appends new ones).
  customOrder: string[]
  // When true, route columns with no stops on the selected service date are
  // hidden from the board. On by default so the board opens decluttered; the
  // routes stay selectable and remain valid move destinations.
  hideZeroStopRoutes: boolean
  // When true, in-progress routes show the COMPLETED / REMAINING / EST /
  // PROGRESS strip under the header. On by default. Off suppresses both the
  // strip and its GPS-timestamps fetch (the metrics are client-computed
  // approximations, so the user can hide them).
  showRouteProgress: boolean
}

const initialState: PersistentBoardV2ViewState = {
  sortMode: 'moreStops',
  customOrder: [],
  hideZeroStopRoutes: true,
  showRouteProgress: true,
}

const persistentBoardV2ViewSlice = createSlice({
  name: 'persistentBoardV2View',
  initialState: { ...initialState },
  reducers: {
    setBoardV2SortMode: (state, action: PayloadAction<BoardV2SortMode>) => {
      state.sortMode = action.payload
    },
    // Seed/replace the custom order — the container calls this to materialize
    // the current visible order the moment custom reordering begins.
    setBoardV2CustomOrder: (state, action: PayloadAction<string[]>) => {
      state.customOrder = action.payload
    },
    // Swap a column one slot left or right within the custom order. The payload
    // order is the authoritative current order so the reducer doesn't depend on
    // possibly-stale slice state.
    moveBoardV2Column: (state, action: PayloadAction<{ order: string[]; routeId: string; direction: MoveDirection }>) => {
      const { order, routeId, direction } = action.payload
      const next = [...order]
      const from = next.indexOf(routeId)
      if (from < 0) return
      const to = direction === 'left' ? from - 1 : from + 1
      if (to < 0 || to >= next.length) return
      ;[next[from], next[to]] = [next[to], next[from]]
      state.customOrder = next
    },
    setBoardV2HideZeroStopRoutes: (state, action: PayloadAction<boolean>) => {
      state.hideZeroStopRoutes = action.payload
    },
    setBoardV2ShowRouteProgress: (state, action: PayloadAction<boolean>) => {
      state.showRouteProgress = action.payload
    },
  },
})

export const { setBoardV2SortMode, setBoardV2CustomOrder, moveBoardV2Column, setBoardV2HideZeroStopRoutes, setBoardV2ShowRouteProgress } =
  persistentBoardV2ViewSlice.actions

export const selectBoardV2SortMode = (state: unknown): BoardV2SortMode =>
  (get(state, 'persistentBoardV2View.sortMode') as BoardV2SortMode) ?? initialState.sortMode

export const selectBoardV2CustomOrder = (state: unknown): string[] =>
  (get(state, 'persistentBoardV2View.customOrder') as string[]) ?? initialState.customOrder

export const selectBoardV2HideZeroStopRoutes = (state: unknown): boolean =>
  (get(state, 'persistentBoardV2View.hideZeroStopRoutes') as boolean) ?? initialState.hideZeroStopRoutes

export const selectBoardV2ShowRouteProgress = (state: unknown): boolean =>
  (get(state, 'persistentBoardV2View.showRouteProgress') as boolean) ?? initialState.showRouteProgress

export default persistentBoardV2ViewSlice.reducer
